/**
 * App-lifetime dark-ship anomaly cache, shared by the flat dark-ship
 * layer and the globe data bridge.
 *
 * AIS anomalies are emitted exactly ONCE per dark period (the detector
 * flags each MMSI), so any cache tied to a renderer's lifecycle loses
 * analyst-visible state on a mode switch. This cache subscribes once
 * and survives flat/globe switches; it is destroyed only at app teardown.
 */

import type { AISAnomaly } from '@/intelligence/analysis/ais-spoofing';
import { eventBus } from '@/core/events';

const cache = new Map<number, AISAnomaly>();

/** Maximum cached dark ships to prevent unbounded growth. */
const MAX_CACHED = 500;

/** TTL for cached dark ship entries (2 hours). */
const CACHE_TTL_MS = 2 * 60 * 60 * 1000;

let cleanupFn: (() => void) | null = null;

/** Subscribe to AIS anomaly events. Idempotent. */
export function initDarkShipCache(): void {
  if (cleanupFn) return;

  cleanupFn = eventBus.on('analysis:ais-anomaly', (anomaly) => {
    if (anomaly.anomalyType !== 'dark-ship' && anomaly.anomalyType !== 'dark-zone') return;
    // Delete-before-set keeps Map iteration in OBSERVATION order, so the
    // oldest-first eviction below cannot evict a just-re-observed ship.
    cache.delete(anomaly.mmsi);
    cache.set(anomaly.mmsi, anomaly);

    // Prune: first TTL-expired entries, then oldest-first until the hard
    // cap holds — the cache is app-lifetime now, so TTL alone would let
    // a busy 2-hour window grow it without bound.
    if (cache.size > MAX_CACHED) {
      const now = Date.now();
      for (const [mmsi, a] of cache) {
        if (now - new Date(a.detectedAt).getTime() > CACHE_TTL_MS) {
          cache.delete(mmsi);
        }
      }
      if (cache.size > MAX_CACHED) {
        for (const mmsi of cache.keys()) {
          if (cache.size <= MAX_CACHED) break;
          cache.delete(mmsi);
        }
      }
    }
  });
}

/** Current dark-ship anomalies (insertion order). */
export function getDarkShips(): AISAnomaly[] {
  return Array.from(cache.values());
}

/** Tear down the subscription and clear state. App teardown only. */
export function destroyDarkShipCache(): void {
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = null;
  }
  cache.clear();
}
